import React from 'react';
import { Building2, Users, Target, Award, Globe, Heart, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Security',
      description: 'Committed to the highest security standards and PCI-DSS compliance.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Building tools for developers and security researchers.'
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'Constantly evolving to meet market needs.'
    },
    {
      icon: Heart,
      title: 'Ethics',
      description: 'Promoting responsible and ethical use of testing technologies.'
    }
  ];

  return (
    <>
      <SEO 
        title="About Us - GENMOZPRO"
        description="Learn about GENMOZPRO, your trusted source for professional payment testing tools. Our mission is to provide developers with reliable BIN generators and card validators."
        keywords="about genmozpro, payment testing tools, developer tools, card validation, bin generator company"
        canonical="https://genmozpro.com/about"
      />
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
            <Building2 className="w-4 h-4 text-purple-500" />
            <span className="text-purple-500 text-sm font-medium">About Us</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            About <span className="text-purple-500">GENMOZPRO</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Providing professional testing tools for developers and security researchers since 2024.
          </p>
        </div>

        <section className="mb-12 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
          <p className="text-gray-300 mb-4">
            GENMOZPRO was created with the goal of providing professional, secure and reliable tools for developers and security researchers to test payment systems. We believe rigorous testing is essential for building safe and robust financial systems.
          </p>
          <p className="text-gray-300">
            Our platform offers a complete suite of tools that allow developers to validate payment integrations, test card validation algorithms and ensure compliance with industry standards, all in a safe and controlled environment.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-purple-500 transition-colors">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-purple-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mb-12 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
            <Globe className="w-6 h-6 text-purple-500" />
            <span>Global Reach</span>
          </h2>
          <p className="text-gray-300 mb-4">
            Our platform is used by developers in more than 50 countries, generating millions of tests monthly. We are committed to providing:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>99.9% uptime with distributed global infrastructure</li>
            <li>Support for multiple card brand standards</li>
            <li>Fast and reliable APIs for seamless integration</li>
            <li>Complete documentation in multiple languages</li>
            <li>LGPD, GDPR and PCI-DSS compliance</li>
          </ul>
        </section>

        <section className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
            <Award className="w-6 h-6 text-purple-500" />
            <span>Commitment to Quality</span>
          </h2>
          <p className="text-gray-300">
            We continuously invest in technology, security and user experience. Our team of experts works tirelessly to ensure you have access to the best testing tools available on the market. We value community feedback and are always evolving to meet your needs.
          </p>
        </section>
      </div>
      </div>
    </>
  );
};

export default About;
